import { Box, FormField, Radio, RadioGroup, TextInput } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useRegionConfig } from "src/utils/use-region-config";

// Discriminator stored in the credential config for the passwordless
// managed-identity method (mirrors AZURE_MANAGED_IDENTITY_METHOD on the backend).
const MANAGED_IDENTITY_METHOD = "managedIdentity";

export const azureConfigValidator = (config): boolean => {
  if (config.method === MANAGED_IDENTITY_METHOD) {
    return Boolean(config.tenant_id);
  }
  return Boolean(config.tenant_id && config.client_id && config.client_secret);
};

/**
 * Azure Form
 * @param config
 * @param setConfig
 * @constructor
 */
export const AzureForm = ({ config, setConfig }) => {
  const regionConfig = useRegionConfig();
  const { azureManagedIdentityAuth } = useFlags();

  // Managed identity is only available to workspaces running in an Azure region
  // and behind the LD flag (enabled per-workspace, like Synapse/Fabric).
  const managedIdentityAvailable =
    regionConfig.regionName.startsWith("azure-") &&
    Boolean(azureManagedIdentityAuth);
  const isManagedIdentity = config.method === MANAGED_IDENTITY_METHOD;

  return (
    <>
      {managedIdentityAvailable && (
        <FormField label="Authentication method" mt={6}>
          <RadioGroup
            orientation="vertical"
            value={config.method ?? "servicePrincipal"}
            onChange={(method) => {
              if (method === MANAGED_IDENTITY_METHOD) {
                // Drop the service-principal secret fields when switching to MI.
                setConfig({
                  method: MANAGED_IDENTITY_METHOD,
                  tenant_id: config.tenant_id,
                });
              } else {
                const { method: _method, ...rest } = config;
                setConfig(rest);
              }
            }}
          >
            <Radio label="Service principal" value="servicePrincipal" />
            <Radio label="Managed identity" value={MANAGED_IDENTITY_METHOD} />
          </RadioGroup>
        </FormField>
      )}

      {isManagedIdentity ? (
        <FormField
          description="Your Microsoft Entra tenant ID (the tenant that owns this storage account). You'll also need to add the Hightouch enterprise application to your tenant and assign it the Storage Blob Data Contributor role on the storage account or container."
          label="Tenant ID"
          mt={6}
        >
          <Box display="flex" gap={3}>
            <TextInput
              value={config.tenant_id ?? ""}
              onChange={(event) =>
                setConfig({ ...config, tenant_id: event.target.value?.trim() })
              }
            />
          </Box>
        </FormField>
      ) : (
        <FormField
          description="Azure → Active Directory → App Registrations"
          label="Configure Azure credentials"
          mt={6}
        >
          <FormField label="Tenant ID">
            <Box display="flex" gap={3}>
              <TextInput
                value={config.tenant_id ?? ""}
                onChange={(event) =>
                  setConfig({
                    ...config,
                    tenant_id: event.target.value?.trim(),
                  })
                }
              />
            </Box>
          </FormField>

          <FormField label="Client ID" mt={6}>
            <Box display="flex" gap={3}>
              <TextInput
                value={config.client_id ?? ""}
                onChange={(event) =>
                  setConfig({
                    ...config,
                    client_id: event.target.value?.trim(),
                  })
                }
              />
            </Box>
          </FormField>

          <FormField label="Client Secret" mt={6}>
            <Box display="flex" gap={3}>
              <TextInput
                type="password"
                value={config.client_secret ?? ""}
                onChange={(event) =>
                  setConfig({ ...config, client_secret: event.target.value })
                }
              />
            </Box>
          </FormField>
        </FormField>
      )}
    </>
  );
};
